import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { useLang } from "../../../i18n/LanguageContext";
import { dreamsClient } from "../api/dreamsClient";
import { extractDreamText, sanitizeDreamHtml } from "../utils/safeDreamHtml";

const DREAMS_PER_PAGE = 12;
const DREAM_EMOTIONS = ["Spokój", "Radość", "Strach", "Smutek", "Złość", "Zaskoczenie"];

const getMyDreamsCopy = (lang) => ({
  lockedTeaser: lang === "en" ? "🔒 This dream is saved in the E2E vault." : "🔒 Ten sen jest zapisany w sejfie E2E.",
  legacyTeaser: lang === "en"
    ? "⚠️ This entry comes from the old storage and has no E2E pack. The new Morfeusz does not show its contents."
    : "⚠️ Ten wpis pochodzi ze starego zapisu i nie ma paczki E2E. Nie pokazuję treści w nowym Morfeuszu.",
  untitled: lang === "en" ? "Untitled dream" : "Sen bez tytułu",
  saved: lang === "en" ? "Dream saved" : "Sen zapisany",
  oldSave: lang === "en" ? "⚠️ OLD SAVE" : "⚠️ STARY ZAPIS",
  gateEyebrow: lang === "en" ? "Dream gate" : "Senna brama",
  gateTitlePre: lang === "en" ? "Welcome to" : "Witaj w",
  gateSubtitle: lang === "en" ? "Sign in to see your Dream Codex." : "Zaloguj się, aby zobaczyć swoją Księgę Snów.",
  signIn: lang === "en" ? "🔑 Sign in" : "🔑 Zaloguj się",
  register: lang === "en" ? "✨ Create account" : "✨ Załóż konto",
  home: lang === "en" ? "🏠 Home" : "🏠 Strona główna",
  loadingEyebrow: lang === "en" ? "Dream Codex" : "Księga Snów",
  loadingTitlePre: lang === "en" ? "Opening" : "Otwieram",
  loadingTitleAccent: lang === "en" ? "your dreams" : "Twoje sny",
  loadingSubtitle: lang === "en" ? "Fetching records from Morfeusz." : "Pobieram zapisy z Morfeusza.",
  fetchError: lang === "en" ? "Could not fetch dreams." : "Nie udało się pobrać snów.",
  title: lang === "en" ? "My dreams" : "Moje sny",
  subtitle: lang === "en" ? "Your Dream Codex — ordered and within reach" : "Twoja Księga Snów — uporządkowana i pod ręką",
  kpiAll: lang === "en" ? "All" : "Wszystkie",
  kpiMonth: lang === "en" ? "This month" : "W tym miesiącu",
  kpiWeek: lang === "en" ? "This week" : "W tym tygodniu",
  kpiAnalysis: lang === "en" ? "With analysis" : "Z analizą",
  filters: lang === "en" ? "Search filters" : "Filtry wyszukiwania",
  filtersHint: lang === "en" ? "click to expand" : "kliknij, aby rozwinąć",
  search: lang === "en" ? "Search" : "Szukaj",
  searchPlaceholder: lang === "en" ? "e.g. lake, house, flying" : "np. jezioro, dom, latanie",
  emotion: lang === "en" ? "Emotion" : "Emocja",
  allEmotions: lang === "en" ? "All" : "Wszystkie",
  symbol: lang === "en" ? "Symbol / tag" : "Symbol / tag",
  symbolPlaceholder: lang === "en" ? "e.g. forest, door, moon" : "np. las, drzwi, księżyc",
  dateFrom: lang === "en" ? "From date" : "Data od",
  dateTo: lang === "en" ? "To date" : "Data do",
  topSymbols: lang === "en" ? "Frequent symbols" : "Najczęstsze symbole",
  clearFilters: lang === "en" ? "Clear filters" : "Wyczyść filtry",
  addDream: lang === "en" ? "Add new dream" : "Dodaj nowy sen",
  backRoom: lang === "en" ? "Back to Room" : "Wróć do Pokoiku",
  emptyTitle: lang === "en" ? "No dreams to display" : "Brak snów do wyświetlenia",
  emptyHint: lang === "en" ? "Add your first dream or change the search filters." : "Dodaj pierwszy sen albo zmień filtry wyszukiwania.",
  addFirst: lang === "en" ? "Add first dream" : "Dodaj pierwszy sen",
  showing: lang === "en" ? "Showing" : "Pokazuję",
  of: lang === "en" ? "of" : "z",
  analysis: lang === "en" ? "Analysis" : "Analiza",
  open: lang === "en" ? "Open" : "Otwórz",
  edit: lang === "en" ? "Edit" : "Edytuj",
  paginationLabel: lang === "en" ? "Dream pagination" : "Paginacja snów",
  previous: lang === "en" ? "Previous" : "Poprzednie",
  next: lang === "en" ? "Next" : "Następne",
});

const getEmotionLabel = (emotion, lang) => {
  if (lang !== "en") return emotion;
  const map = { Spokój: "Calm", Radość: "Joy", Strach: "Fear", Smutek: "Sadness", Złość: "Anger", Zaskoczenie: "Surprise" };
  return map[emotion] || emotion;
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return value.split(",").map((item) => item.trim()).filter(Boolean);
  return [];
};

const normalizeFilterText = (value) => String(value || "").trim().toLowerCase();

const isVaultUnlocked = () => {
  if (typeof window === "undefined") return false;
  try { return Boolean(window.MDVaultSession?.getState?.()?.unlocked || window.MDVaultSession?.isUnlocked?.()); } catch (_) { return false; }
};

const getDreamE2E = (dream) => dream?.e2e || { iv: "", ct: "", aad: "" };

const normalizeDream = (dream, decrypted = null, copy) => {
  const meta = dream.meta || {};
  const e2e = getDreamE2E(dream);
  const hasE2E = Boolean(e2e.iv && e2e.ct);
  const bodyHtml = hasE2E && decrypted?.bodyHtml ? sanitizeDreamHtml(decrypted.bodyHtml) : "";
  const bodyText = extractDreamText(bodyHtml);
  const date = dream.date || meta.date || dream.sen_data || "";
  const time = dream.time || meta.time || dream.sen_godzina || "";
  const createdAt = dream.createdAt || dream.updatedAt || [date, time].filter(Boolean).join("T") || "";
  const emotions = normalizeArray(dream.emotions || meta.emotions || dream.sen_emocje || []);
  const symbols = normalizeArray(dream.symbols || meta.symbols || dream.sen_symbole || "");
  const unlockedText = hasE2E && decrypted?.bodyHtml ? bodyText : "";

  return {
    id: dream.id || dream.wpId,
    title: dream.title || dream.sen_tytul || meta.title || copy.untitled,
    date,
    time,
    dateHuman: [date, time].filter(Boolean).join(" · ") || copy.saved,
    createdAt,
    saveMode: hasE2E ? "e2e" : "legacy",
    saveBadge: hasE2E ? "" : copy.oldSave,
    bodyText,
    teaser: hasE2E ? (unlockedText ? (unlockedText.length > 220 ? `${unlockedText.slice(0, 220).trim()}…` : unlockedText) : copy.lockedTeaser) : copy.legacyTeaser,
    teaserHtml: hasE2E && unlockedText ? bodyHtml : "",
    emotions,
    symbols,
    hasAnalysis: Boolean(dream.analysis || dream.analysisHtml || dream._analiza_ai),
    hasFollowup: Boolean(dream.followup?.used || dream._followup_a),
    hasE2E,
    e2e,
  };
};

const countThisMonth = (dreams) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const prefix = `${yyyy}-${mm}`;
  return dreams.filter((dream) => String(dream.date || dream.createdAt || "").startsWith(prefix)).length;
};

const countThisWeek = (dreams) => {
  const now = new Date();
  const start = new Date(now);
  const day = start.getDay() || 7;
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day + 1);
  return dreams.filter((dream) => {
    const raw = dream.createdAt || dream.date;
    if (!raw) return false;
    const d = new Date(raw);
    return !Number.isNaN(d.getTime()) && d >= start;
  }).length;
};

const buildPageNumbers = (page, pageCount) => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const numbers = new Set([1, pageCount, page - 1, page, page + 1]);
  return [...numbers].filter((item) => item >= 1 && item <= pageCount).sort((a, b) => a - b);
};

const scrollToMyDreamsTop = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  window.requestAnimationFrame(() => {
    const root = document.getElementById("moje-sny");
    const headerOffset = document.querySelector("header")?.getBoundingClientRect?.().height || 0;
    const top = root ? root.getBoundingClientRect().top + window.scrollY - Math.max(0, headerOffset + 18) : 0;
    window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "auto" });
  });
};

const PokoikGate = ({ copy }) => (
  <section className="mdPage" data-ui="gate"><div className="mdPage__inner"><div><p className="mdPage__eyebrow">{copy.gateEyebrow}</p><h1 className="mdPage__title">{copy.gateTitlePre} <em>Morfeusz Dreams</em></h1><p className="mdPage__subtitle">{copy.gateSubtitle}</p></div><div className="mdCard"><div className="mdRow"><a className="mdBtn mdBtn--primary mdBtn--row" data-md-login="1" href="#/zaloguj?redirect_to=/moje-sny">{copy.signIn}</a><a className="mdBtn mdBtn--row" href="#/rejestracja?redirect_to=/moje-sny">{copy.register}</a><a className="mdBtn mdBtn--row" href="#top">{copy.home}</a></div></div></div></section>
);

const LoadingState = ({ copy }) => (
  <section className="mdPage" data-ui="my-dreams-loading"><div className="mdPage__inner"><div className="mdCard" data-ui="my-dreams-loading-card"><p className="mdPage__eyebrow">{copy.loadingEyebrow}</p><h1 className="mdPage__title">{copy.loadingTitlePre} <em>{copy.loadingTitleAccent}</em></h1><p className="mdPage__subtitle">{copy.loadingSubtitle}</p></div></div></section>
);

const MyDreamsActions = ({ copy }) => (
  <div className="md-cta-row" data-ui="moje-sny-actions">
    <a className="md-btn md-primary" href="#/dodaj-sen">{copy.addDream}</a>
    <a className="md-btn md-secondary" href="#/pokoik">{copy.backRoom}</a>
  </div>
);

export const MyDreamsPage = () => {
  const { lang } = useLang();
  const { isAuthenticated, authReady } = useAuth();
  const copy = useMemo(() => getMyDreamsCopy(lang), [lang]);
  const [query, setQuery] = useState("");
  const [emotion, setEmotion] = useState("all");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [dreamsRaw, setDreamsRaw] = useState([]);
  const [decryptedDreams, setDecryptedDreams] = useState({});
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadDreams = async () => {
      if (!authReady) return;
      if (!isAuthenticated) {
        setDreamsRaw([]); setDecryptedDreams({}); setLoading(false); return;
      }
      setLoading(true); setNotice("");
      try {
        const response = await dreamsClient.list();
        if (mounted) setDreamsRaw(Array.isArray(response.dreams) ? response.dreams : []);
      } catch (error) {
        if (mounted) { setNotice(error?.message || copy.fetchError); setDreamsRaw([]); }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadDreams();
    return () => { mounted = false; };
  }, [authReady, isAuthenticated, copy.fetchError]);

  useEffect(() => {
    let cancelled = false;
    const decryptUnlockedDreams = async () => {
      if (!isAuthenticated || !isVaultUnlocked()) return;
      const e2eDreams = dreamsRaw.filter((dream) => {
        const pack = getDreamE2E(dream);
        return pack.iv && pack.ct;
      });
      if (!e2eDreams.length) return;
      const next = {};
      for (const dream of e2eDreams) {
        const id = dream.id || dream.wpId;
        if (!id || decryptedDreams[id]?.bodyHtml) continue;
        try {
          const pack = getDreamE2E(dream);
          const plain = await window.morfeuszE2E.decryptText(pack.iv, pack.ct, pack.aad || "");
          next[id] = { bodyHtml: sanitizeDreamHtml(plain) };
        } catch (_) { next[id] = { error: true }; }
      }
      if (!cancelled && Object.keys(next).length) setDecryptedDreams((current) => ({ ...current, ...next }));
    };
    decryptUnlockedDreams();
    window.addEventListener("morfeusz:vault:session", decryptUnlockedDreams);
    window.addEventListener("pageshow", decryptUnlockedDreams);
    return () => { cancelled = true; window.removeEventListener("morfeusz:vault:session", decryptUnlockedDreams); window.removeEventListener("pageshow", decryptUnlockedDreams); };
  }, [dreamsRaw, decryptedDreams, isAuthenticated]);

  const dreams = useMemo(() => dreamsRaw.map((dream) => normalizeDream(dream, decryptedDreams[dream.id || dream.wpId], copy)).sort((a, b) => String(b.createdAt || b.id).localeCompare(String(a.createdAt || a.id))), [dreamsRaw, decryptedDreams, copy]);

  const popularSymbols = useMemo(() => {
    const counts = new Map();
    dreams.forEach((dream) => dream.symbols.forEach((symbol) => {
      const clean = String(symbol || "").trim();
      if (!clean) return;
      const key = clean.toLowerCase();
      const current = counts.get(key) || { label: clean, count: 0 };
      current.count += 1;
      counts.set(key, current);
    }));
    return [...counts.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)).slice(0, 8);
  }, [dreams]);

  const filteredDreams = useMemo(() => {
    const q = normalizeFilterText(query);
    const symbolQ = normalizeFilterText(symbolFilter).replace(/^#/, "");
    return dreams.filter((dream) => {
      if (emotion !== "all" && !dream.emotions.includes(emotion)) return false;
      if (dateFrom && String(dream.date || dream.createdAt || "").slice(0, 10) < dateFrom) return false;
      if (dateTo && String(dream.date || dream.createdAt || "").slice(0, 10) > dateTo) return false;
      if (symbolQ && !dream.symbols.some((symbol) => normalizeFilterText(symbol).includes(symbolQ))) return false;
      if (!q) return true;
      const haystack = [dream.title, dream.bodyText, dream.symbols.join(" "), dream.emotions.join(" ")].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [dreams, emotion, query, symbolFilter, dateFrom, dateTo]);

  useEffect(() => { setPage(1); scrollToMyDreamsTop(); }, [emotion, query, symbolFilter, dateFrom, dateTo]);

  if (!authReady || loading) return <LoadingState copy={copy} />;
  if (!isAuthenticated) return <PokoikGate copy={copy} />;

  const pageCount = Math.max(1, Math.ceil(filteredDreams.length / DREAMS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * DREAMS_PER_PAGE;
  const pageDreams = filteredDreams.slice(pageStart, pageStart + DREAMS_PER_PAGE);
  const pageNumbers = buildPageNumbers(safePage, pageCount);
  const goToPage = (nextPage) => { setPage(Math.min(Math.max(1, nextPage), pageCount)); scrollToMyDreamsTop(); };
  const total = dreams.length;
  const withAnalysis = dreams.filter((dream) => dream.hasAnalysis).length;
  const thisMonth = countThisMonth(dreams);
  const thisWeek = countThisWeek(dreams);
  const activeFilters = (query.trim() ? 1 : 0) + (emotion !== "all" ? 1 : 0) + (symbolFilter.trim() ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);
  const clearFilters = () => { setQuery(""); setEmotion("all"); setSymbolFilter(""); setDateFrom(""); setDateTo(""); };

  return (
    <section className="dream-container" id="moje-sny" data-ui="moje-sny">
      <header className="dream-header" data-ui="moje-sny-header"><div className="dream-icon" aria-hidden="true">📖</div><h1 className="dream-title" data-ui="moje-sny-title">{copy.title}</h1><div className="dream-date">{copy.subtitle}</div></header>
      {notice && <div className="md-alert md-info" data-ui="moje-sny-notice" role="status">{notice}</div>}
      <div data-ui="moje-sny-kpis"><div className="md-card md-fade" data-ui="kpi-all"><div data-ui="kpi-num">{total}</div><div data-ui="kpi-lbl">{copy.kpiAll}</div></div><div className="md-card md-fade" data-ui="kpi-month"><div data-ui="kpi-num">{thisMonth}</div><div data-ui="kpi-lbl">{copy.kpiMonth}</div></div><div className="md-card md-fade" data-ui="kpi-week"><div data-ui="kpi-num">{thisWeek}</div><div data-ui="kpi-lbl">{copy.kpiWeek}</div></div><div className="md-card md-fade" data-ui="kpi-analysis"><div data-ui="kpi-num">{withAnalysis}</div><div data-ui="kpi-lbl">{copy.kpiAnalysis}</div></div></div>
      <details className="dream-content-card" data-ui="moje-sny-filters">
        <summary data-ui="moje-sny-filters-summary"><span>🔎 {copy.filters}</span><span data-ui="moje-sny-filters-hint">{copy.filtersHint}</span>{activeFilters > 0 && <span data-ui="moje-sny-filters-count">{activeFilters}</span>}<span aria-hidden="true" data-ui="moje-sny-filters-arrow">▾</span></summary>
        <div data-ui="moje-sny-filters-body">
          <div data-ui="moje-sny-search-grid"><div className="field"><label htmlFor="my-dreams-search">{copy.search}</label><input id="my-dreams-search" className="md-input" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.searchPlaceholder} /></div><div className="field"><label htmlFor="my-dreams-symbol">{copy.symbol}</label><input id="my-dreams-symbol" className="md-input" type="search" value={symbolFilter} onChange={(event) => setSymbolFilter(event.target.value)} placeholder={copy.symbolPlaceholder} /></div><div className="field"><label htmlFor="my-dreams-emotion">{copy.emotion}</label><select id="my-dreams-emotion" className="md-input" value={emotion} onChange={(event) => setEmotion(event.target.value)}><option value="all">{copy.allEmotions}</option>{DREAM_EMOTIONS.map((item) => <option key={item} value={item}>{getEmotionLabel(item, lang)}</option>)}</select></div><div className="field"><label htmlFor="my-dreams-date-from">{copy.dateFrom}</label><input id="my-dreams-date-from" className="md-input" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></div><div className="field"><label htmlFor="my-dreams-date-to">{copy.dateTo}</label><input id="my-dreams-date-to" className="md-input" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></div></div>
          {(popularSymbols.length > 0 || activeFilters > 0) && <div data-ui="moje-sny-filter-footer">{popularSymbols.length > 0 && <div data-ui="moje-sny-symbol-cloud"><span data-ui="moje-sny-symbol-cloud-label">{copy.topSymbols}</span>{popularSymbols.map((item) => <button type="button" className="md-chip" data-ui="moje-sny-symbol-chip" key={item.label} onClick={() => setSymbolFilter(item.label)}>#{item.label}</button>)}</div>}{activeFilters > 0 && <button type="button" className="md-btn md-secondary" data-ui="moje-sny-clear-filters" onClick={clearFilters}>{copy.clearFilters}</button>}</div>}
        </div>
      </details>
      {filteredDreams.length === 0 ? <section className="dream-content-card" data-ui="moje-sny-empty" role="status"><div className="dream-icon" aria-hidden="true">💤</div><h2 className="dream-section-title">{copy.emptyTitle}</h2><p className="md-hint">{copy.emptyHint}</p><div className="md-cta-row"><a className="md-btn md-primary" href="#/dodaj-sen">{copy.addFirst}</a></div></section> : <>
        <div data-ui="moje-sny-count">{copy.showing} {pageStart + 1}–{Math.min(pageStart + DREAMS_PER_PAGE, filteredDreams.length)} {copy.of} {filteredDreams.length}</div>
        <div data-ui="moje-sny-grid">{pageDreams.map((dream) => <article className="md-card md-fade" data-ui="moje-sny-card" key={dream.id} data-save-mode={dream.saveMode}><header data-ui="moje-sny-card-head"><div className="dream-icon" aria-hidden="true">🌙</div><div><h2 data-ui="moje-sny-card-title" title={dream.title}><span>{dream.title}</span></h2><time>{dream.dateHuman}</time></div></header><div className="md-cta-row" data-ui="dream-save-badge-row">{dream.saveBadge && <span className="md-chip" data-ui="dream-save-badge">{dream.saveBadge}</span>}{dream.hasAnalysis && <span className="md-chip">{copy.analysis}</span>}{dream.hasFollowup && <span className="md-chip">Follow-Up</span>}</div><p data-ui="moje-sny-card-excerpt">{dream.teaser}</p>{(dream.emotions.length > 0 || dream.symbols.length > 0) && <div data-ui="moje-sny-card-tags">{dream.emotions.slice(0, 3).map((item) => <span className="md-chip" key={`e-${item}`}>{getEmotionLabel(item, lang)}</span>)}{dream.symbols.slice(0, 3).map((item) => <span className="md-chip" key={`s-${item}`}>#{item}</span>)}</div>}<div data-ui="moje-sny-card-cta"><a className="md-btn md-primary" href={`#/sen/${encodeURIComponent(dream.id)}`}>{copy.open}</a><a className="md-btn md-secondary" href={`#/sen/${encodeURIComponent(dream.id)}?edit=1`}>{copy.edit}</a></div></article>)}</div>
        {pageCount > 1 && <nav data-ui="moje-sny-pagination" aria-label={copy.paginationLabel}><button type="button" className="md-btn md-secondary" disabled={safePage === 1} onClick={() => goToPage(safePage - 1)}>{copy.previous}</button><div data-ui="moje-sny-pages">{pageNumbers.map((pageNumber, index) => { const previous = pageNumbers[index - 1]; const needsGap = previous && pageNumber - previous > 1; return <span key={pageNumber} data-ui="moje-sny-page-wrap">{needsGap && <span data-ui="moje-sny-page-gap">…</span>}<button type="button" className={`md-btn md-secondary ${pageNumber === safePage ? "is-active" : ""}`} data-active={pageNumber === safePage ? "1" : "0"} aria-current={pageNumber === safePage ? "page" : undefined} onClick={() => goToPage(pageNumber)}>{pageNumber}</button></span>; })}</div><button type="button" className="md-btn md-secondary" disabled={safePage === pageCount} onClick={() => goToPage(safePage + 1)}>{copy.next}</button></nav>}
      </>}
      <MyDreamsActions copy={copy} />
    </section>
  );
};
